import React, { useState, useEffect } from 'react';
import UI from '../../ui';
import { Instructions } from '../Instructions';
import { ValidationErrors } from '../Validations';
import Data from '../../data';

const { BarcodeTypesList, BarcodeTypes, CalculatedData } = Data;

const {
	WrappedDropdownWithLabel,
	WrappedInputNumberWithLabel,
	WrappedInputTextWithLabel,
	WrappedButton,
	WrappedButtonType,
} = UI;

const ProductItem = ({
	productOptions,
	showMobileView,
	isMobile,
	onActionTrigger,
	productIndex,
	productItem,
	onDeleteButtonClick,
	updateProductInstructions,
	validations,
	updateValidation,
}) => {
	const { productId, barcode, quantityExpected } = productItem;
	let productPanelInterval = null;
	let productValidations = validations.product[productIndex] || {};

	const [quantityErrorMessage, setQuantityErrorMessage] = useState(
		ValidationErrors.quantityRequired
	);
	const [productBarcode, setProductBarcode] = useState({
		barcode: '',
		type: BarcodeTypes.UPC,
	});
	const [disableBarcode, setDisableBarcode] = useState(false);
	const [barcodeValidationMessage, setBarcodeValidationMessage] = useState(
		ValidationErrors.barcodeRequired
	);

	const updateProductValidation = () => {
		const updatedValidations = validations;
		let productValidation = 0,
			quantityValidation = 0,
			barcodeValidation = 0;

		if (productValidations.product !== 0) {
			productValidation = productItem.sku ? 1 : -1;
		}

		if (productValidations.quantity !== 0) {
			if (productItem.quantityExpected === 0) {
				quantityValidation = -1;
				setQuantityErrorMessage(ValidationErrors.quantityZero);
			} else {
				quantityValidation = productItem.quantityExpected ? 1 : -1;
				setQuantityErrorMessage(ValidationErrors.quantityRequired);
			}
		}

		if (productValidations.barcode !== 0) {
			barcodeValidation = productBarcode ? 1 : -1;
		}

		productValidations = {
			product: productValidation,
			quantity: quantityValidation,
			barcode: barcodeValidation,
		};

		updatedValidations.product[productIndex] = productValidations;

		updateValidation(updatedValidations);
		return updatedValidations;
	};

	const getProductBarcode = () => {
		const product = productOptions.find(record => record.productId === productId);
		let barcodeType = BarcodeTypes.UPC;

		if (product && product.barcode) {
			setProductBarcode({
				barcode: product.barcode,
				type: product.barcodeType,
			});
			setDisableBarcode(true);
		} else {
			if (barcode && barcode.type) {
				barcodeType = barcode.type;
			} else if (barcode && barcode.barcode) {
				barcodeType = CalculatedData.getBarcodeType(barcode.barcode);
			}

			setProductBarcode({
				barcode: barcode ? barcode.barcode : '',
				type: barcodeType,
			});
			setDisableBarcode(false);
		}
	};

	useEffect(() => {
		getProductBarcode();
		updateProductValidation();
	}, [productId, barcode, quantityExpected]);

	const validateProduct = selectedProduct => {
		const updatedValidations = validations;
		const product = productOptions.find(record => record.productId === selectedProduct);

		productValidations.product = product ? 1 : -1;
		updatedValidations.product[productIndex] = productValidations;

		updateValidation(updatedValidations);
	};

	const validateBarcode = selectedBarcode => {
		const updatedValidations = validations;
		const barcodeIndex = productOptions.findIndex(
			product =>
				product.barcode !== '' &&
				product.barcode.toLowerCase() === selectedBarcode.toLowerCase()
		);

		if (barcodeIndex !== -1) {
			productValidations.barcode = -1;
			setBarcodeValidationMessage(ValidationErrors.barcodeDuplicate);
		} else {
			productValidations.barcode = selectedBarcode ? 1 : -1;
			setBarcodeValidationMessage(ValidationErrors.barcodeRequired);
		}
		updatedValidations.product[productIndex] = productValidations;

		updateValidation(updatedValidations);
	};

	const onProductBlur = () => {
		const panel = document.getElementsByClassName('p-input-overlay-visible')[0];
		const checkIfPanelClosed = () => {
			// Clear interval and validate once dropdown panel is no longer displayed
			if (window.getComputedStyle(panel).display === 'none') {
				clearInterval(productPanelInterval);
				validateProduct(productId);
			}
		};

		// Blur event is triggered when dropdown input loses focus, including when dropdown gets opened. This will
		// create an interval to check for when the dropdown panel is no longer displayed, and then perform validation
		if (panel) {
			productPanelInterval = setInterval(() => checkIfPanelClosed(), 1000);
		}
	};

	const onProductChange = e => {
		const { value, name } = e.target;

		clearInterval(productPanelInterval);
		validateProduct(value);
		validateBarcode(productBarcode.barcode);
		onActionTrigger(productIndex, name, value);
	};

	const onBarcodeBlur = () => {
		validateBarcode(productBarcode.barcode);
	};

	const onBarCodeChange = e => {
		const { value, name } = e.target;
		const updatedBarcode = productBarcode;
		updatedBarcode[name] = value;
		if (name === 'barcode') {
			updatedBarcode.type = CalculatedData.getBarcodeType(value);
		}
		onActionTrigger(productIndex, barcode, updatedBarcode);
	};

	const validateQuantity = value => {
		const updatedValidations = validations;
		let quantityValidation = 0;

		if (value === 0) {
			quantityValidation = -1;
			setQuantityErrorMessage(ValidationErrors.quantityZero);
		} else {
			quantityValidation = value ? 1 : -1;
			setQuantityErrorMessage(ValidationErrors.quantityRequired);
		}

		productValidations.quantity = quantityValidation;
		updatedValidations.product[productIndex] = productValidations;
		updateValidation(updatedValidations);
	};

	const onQuantityBlur = e => {
		const { value, name } = e.target;

		if (value && parseInt(value) !== quantityExpected) {
			validateQuantity(parseInt(value));
			onActionTrigger(productIndex, name, parseInt(value));
		} else {
			validateQuantity(quantityExpected);
		}
	};

	const onQuantityChange = e => {
		const { value, name } = e.target;

		validateQuantity(value);
		onActionTrigger(productIndex, name, value);
	};

	const onDelete = () => {
		onDeleteButtonClick(productIndex);
	};

	const onProductFocus = () => {
		updateProductInstructions(
			Instructions.headers.product.product,
			Instructions.instructions.product.product
		);
	};

	const onQuantityFocus = () => {
		updateProductInstructions(
			Instructions.headers.product.quantity,
			Instructions.instructions.product.quantity
		);
	};

	const onBarCodeFocus = () => {
		updateProductInstructions(
			Instructions.headers.product.barcode,
			Instructions.instructions.product.barcode
		);
	};

	const mobileClassName = isMobile ? 'mobile-wrapper' : '';

	return (
		<div
			className={`p-grid p-col-12 product-item ${showMobileView ? 'p-dir-col' : 'p-dir-row'}`}
		>
			{showMobileView && (
				<WrappedButton
					buttonType={WrappedButtonType.Icon}
					icon="delete-icon"
					className="p-col p-col-align-end delete-product"
					onClick={onDelete}
					dataqa="product-item-delete"
				/>
			)}

			<WrappedDropdownWithLabel
				className={`${showMobileView ? 'p-col-12' : 'p-md-5 p-lg-5'} ${mobileClassName}`}
				label={showMobileView ? 'Product' : ''}
				isInputInvalid={productValidations.product === -1}
				showErrorBelow={isMobile}
				errorMessage={ValidationErrors.productRequired}
				uiElementConfig={{
					name: 'productId',
					options: productOptions,
					optionLabel: 'productLabel',
					optionValue: 'productId',
					value: productId,
					onChange: onProductChange,
					onFocus: onProductFocus,
					onBlur: onProductBlur,
					dataqa: 'inbound-product-details-dropdown-options',
					filter: productOptions && productOptions.length > 0,
					filterPlaceholder: 'Search Products',
					filterBy: 'productLabel,barcode',
					placeholder: 'Select a Product',
				}}
			/>

			<WrappedInputNumberWithLabel
				className={`${
					showMobileView ? 'p-col-12' : 'p-md-2 p-lg-2'
				} ${mobileClassName} quantity`}
				label={showMobileView ? 'Quantity' : ''}
				isInputInvalid={productValidations.quantity === -1}
				showErrorBelow={isMobile}
				errorMessage={quantityErrorMessage}
				uiElementConfig={{
					dataqa: 'inbound-product-details-quantity',
					name: 'quantityExpected',
					value: quantityExpected,
					min: 0,
					incrementButtonIcon: 'pi pi-chevron-up',
					decrementButtonIcon: 'pi pi-chevron-down',
					showButtons: true,
					onChange: onQuantityChange,
					onFocus: onQuantityFocus,
					onBlur: onQuantityBlur,
				}}
			/>

			<WrappedInputTextWithLabel
				label={showMobileView ? 'Barcode' : ''}
				className={`${
					showMobileView ? 'p-col-12 barcode' : 'p-md-3 p-lg-3'
				} ${mobileClassName} barcode`}
				isInputInvalid={productValidations.barcode === -1}
				showErrorBelow={isMobile}
				errorMessage={barcodeValidationMessage}
				uiElementConfig={{
					name: 'barcode',
					value: productBarcode.barcode,
					onChange: onBarCodeChange,
					onFocus: onBarCodeFocus,
					onBlur: onBarcodeBlur,
					dataqa: 'inbound-product-barcode',
					disabled: disableBarcode,
				}}
			/>

			<WrappedDropdownWithLabel
				className={`${
					showMobileView ? 'p-col-12 barcode' : 'p-md-2 p-lg-2'
				} ${mobileClassName} barcode`}
				label={showMobileView ? 'Barcode Type' : ''}
				uiElementConfig={{
					name: 'type',
					options: BarcodeTypesList,
					optionLabel: 'barcodeType',
					optionValue: 'barcodeType',
					value: productBarcode.type,
					placeholder: 'Barcode Value',
					onChange: onBarCodeChange,
					dataqa: 'inbound-product-barcode-options',
					disabled: disableBarcode,
				}}
			/>

			{!showMobileView && (
				<WrappedButton
					buttonType={WrappedButtonType.Icon}
					icon="delete-icon"
					className="p-lg-1 p-md-1 delete-product"
					onClick={onDelete}
					dataqa="product-item-delete"
				/>
			)}
		</div>
	);
};

export default ProductItem;
